import React from "react";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import SectionAccents from "@/components/SectionAccents";

const Contact = () => {
    const { settings } = useSettings();
    const contact = settings?.contact || {};
    const waLink = `https://wa.me/${(contact.whatsapp || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Hi Cherishables! I'd love to know more about custom 3D miniatures.")}`;

    return (
        <div className="relative max-w-5xl mx-auto px-6 py-16 overflow-hidden" data-testid="contact-page">
            <SectionAccents variant="contact" />
            <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">We'd love to hear from you</div>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tighter mt-1">Say hi.</h1>
            <p className="text-muted-foreground mt-3 max-w-xl">Questions, custom orders, bulk enquiries — reach out on WhatsApp or drop us an email and our team will get back within a day.</p>

            <div className="mt-10 grid md:grid-cols-2 gap-5">
                <InfoCard icon={<Mail className="w-4 h-4" />} label="Email" value={contact.email} href={contact.email ? `mailto:${contact.email}` : ""} testId="contact-email" />
                <InfoCard icon={<Phone className="w-4 h-4" />} label="Phone" value={contact.phone} href={contact.phone ? `tel:${contact.phone.replace(/\s/g, "")}` : ""} testId="contact-phone" />
                <InfoCard icon={<MessageCircle className="w-4 h-4" />} label="WhatsApp" value={formatWhatsapp(contact.whatsapp)} href={waLink} testId="contact-whatsapp" accent />
                <InfoCard icon={<MapPin className="w-4 h-4" />} label="Studio" value={contact.address} testId="contact-address" />
                <div className="rounded-3xl glass p-5 md:col-span-2" data-testid="contact-hours">
                    <div className="text-xs font-semibold uppercase tracking-widest mb-1 text-muted-foreground">Business Hours</div>
                    <div className="text-sm">{contact.business_hours}</div>
                </div>
                {contact.map_embed_url && (
                    <div className="rounded-3xl glass p-2 aspect-video overflow-hidden md:col-span-2" data-testid="contact-map">
                        <iframe src={contact.map_embed_url} title="map" className="w-full h-full rounded-2xl" />
                    </div>
                )}
            </div>
        </div>
    );
};

const InfoCard = ({ icon, label, value, href, testId, accent }) => (
    <div className={`rounded-3xl glass p-5 flex items-start gap-3 transition ${accent ? "hover:bg-[#25D366]/10" : "hover:bg-primary/5"}`} data-testid={testId}>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${accent ? "bg-[#25D366]/15 text-[#25D366]" : "bg-primary/10 text-primary"}`}>{icon}</div>
        <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
            {href ? (
                <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={`text-sm hover:underline break-all ${accent ? "text-[#25D366] font-semibold" : "text-primary"}`}>
                    {value}
                </a>
            ) : (
                <div className="text-sm break-words">{value}</div>
            )}
        </div>
    </div>
);

const formatWhatsapp = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/[^0-9]/g, "");
    if (digits.length === 12 && digits.startsWith("91")) return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
    if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
    return `+${digits}`;
};

export default Contact;
